import './App.css';
// hootdex.com look & feel - must come after App.css.
import './theme/hootdex-theme.css';
import OrbBackground from './components/OrbBackground/OrbBackground';
import Home from './screens/home';
import Nav from './components/nav/nav';
import Footer from './components/footer/footer';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams
} from 'react-router-dom';
import Login from './screens/login';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePoll } from './hooks/usePoll';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import DashboardIndex from './screens/DashboardIndex';
import PoolPage from './modules/Pools/pools.page';
import { api as axios } from './api';
import CreatePool from './screens/CreatePool';
import ProjectToken from './modules/Projects/project.page';
import * as actionTypes from './store/actionTypes';
import { fetchTokenList, verifyToken } from './utils';
import News from './screens/news';
import Commodity from './components/Tables/commodity';
import CommodityPage from './screens/commodityPAge';
import SyntheticPage from './modules/Synthetics/synthetics.page';
import PoolTokensUpdate from './components/Tables/editpools';
import DelistedToken from './components/Tables/DelistedToken';
import RedirectPage from './authExchange';
import CreateDAT from './modules/DAT/createDAT';
import DatTable from './modules/DAT/dat.table';
import DatPage from './modules/DAT/dat.page';
import ProjectTable from './modules/Projects/project.table';
import CryptopairTable from './modules/CryptoPair/cryptopair.table';
import CryptoPairPage from './modules/CryptoPair/cryptopair.page';
import WrapTable from './modules/Wraps/wrap.table';
import WrapPage from './modules/Wraps/wrap.page';
import PecuPage from './modules/Pecu/pecu.page';
import { HelmetProvider } from 'react-helmet-async';
import CreateHedge from './modules/hedge/createHedge';
import FanTable from './modules/Projects/fan.table';
import CreateTreasuryForm from './screens/CreateTreasuryForm';
import StablecoinsTable from './modules/stablecoins/stablecoins.table';
import EquiTrackTable from './modules/EquiTrack/equitrack.table';
import CreateTokensv1 from './screens/createTokensv1';
import StableCoinPage from './modules/stablecoins/stablecoin.page';
import EquiTrackPage from './modules/EquiTrack/equitrack.page';
import StableCoinPoolPage from './modules/Pools/pools.page.stable.coin';
import CreateLeverageTokenForm from './screens/createLeverageTokenForm';
import LeverageToken from './modules/leverageTokens/leverageToken';
import PdcnTokensTable from './modules/PDCN/pdcnToken';
import DcnTokensTable from './modules/DCN/dcnToken';
import CreatePDCNTokenForm from './screens/createPDCNTokenForm';
import PDCNPage from './modules/PDCN/pdcn.page';
import CreateDCNTokenForm from './screens/CreateDCNTokenForm';
import DCNPage from './modules/DCN/dcn.page';
import MostActiveTicker, {
  TICKER_HEIGHT
} from './components/MostActiveTicker/MostActiveTicker';

// Sends an old-name URL (/cryptoPairs/X, /FanToken/X) to its renamed route.
function LegacyRedirect({ to, param }) {
  const params = useParams();
  return (
    <Navigate to={`${to}/${encodeURIComponent(params[param] || '')}`} replace />
  );
}

function App() {
  const { UserInfo, WalletInfo, isAdmin } = useSelector(
    (state) => ({
      UserInfo: state.UserInfo,
      WalletInfo: state.WalletInfo,
      isAdmin: state.isAdmin
    }),
    shallowEqual
  );
  const [pecuCoins, setPecuCoins] = useState({});
  const dispatch = useDispatch();
  // Last known-good orderBookDepth — reused when markets.json fails/times out
  // so a transient error doesn't flash every OBD figure in the UI to 0.
  const orderBookDepthRef = useRef({});

  const fetchWallet = useCallback(async () => {
    if (WalletInfo?.userFound) {
      try {
        const response = await axios.get(
          `/hootdex/getMycoins/${WalletInfo.uid}`
        );
        setPecuCoins(response.data[0]);
      } catch (error) {
        console.error('Error fetching wallet:', error);
      }
    }
  }, [WalletInfo]);

  useEffect(() => {
    if (WalletInfo?.userFound) {
      fetchWallet();
    }
  }, [WalletInfo, fetchWallet]);

  const fetchPecuPrice = () => {
    axios
      .get(`/wallet/get_current_index_coin`)
      .then((res) => {
        const price = res.data[0]?.value;
        dispatch({ type: actionTypes.PECU_PRICE, price });
      })
      .catch((err) => console.error(err));
  };

  const fetchCryptoData = useCallback(() => {
    axios.get(`/crypto/index`).then((res) => {
      dispatch({ type: actionTypes.CRYPTO_DATA, cryptoData: res.data });
    });
  }, [dispatch]);

  usePoll(() => Promise.all([fetchPecuPrice(), fetchCryptoData()]), 10_000, [
    fetchCryptoData
  ]);

  // Global stats — polled once here every 30 s so every TokenTable instance
  // can read from Redux instead of each making its own requests.
  //
  // orderBookDepth (per-token Order Book Depth: sum of live bids+asks, right
  // now) used to be fetched straight from api.hootdex.org/markets.json in the
  // browser. It now comes from /wallet/platform-stats, which measures the same
  // thing directly off Hootdex's resting-order collections.
  //
  // Two reasons for the move. The server cannot reach api.hootdex.org at all
  // (that fetch times out server-side, even though a browser can make it), so
  // the value was unsnapshottable and its 24h change uncomputable. And with
  // the dashboard's OBD card served from the backend, leaving this table on a
  // second, independent source would let the headline figure and the per-row
  // column disagree. One source now feeds both.
  //
  // Hootdex is a CLOB, not an AMM - there is no real per-token TVL, so
  // TokenTable's per-row column reads this rather than an amount*price
  // notional guess. `tvl` (from /wallet/tvl) is the platform-wide figure only.
  usePoll(
    async () => {
      const [hourlyTrend, dailyTrend, volumeRes, tvlRes, platformRes] =
        await Promise.all([
          axios.get(`/wallet/get_change_index_coin_hourly`),
          axios.get(`/wallet/get_change_index_coin`),
          axios.get(`/wallet/calculate_daily_volume`),
          axios.get(`/wallet/tvl`),
          axios.get(`/wallet/platform-stats`).catch((err) => {
            console.error(
              '[App] platform-stats fetch failed (orderBookDepth will keep its last known value):',
              err.message
            );
            return null;
          })
        ]);

      // platformRes is null when the request failed - fall back to the last
      // successful reading instead of overwriting it with zeros. Same guard
      // applies when the endpoint responds but its OBD source is down, in
      // which case byMarket comes back empty.
      let orderBookDepth = orderBookDepthRef.current;
      const byMarket = platformRes?.data?.orderBookDepth?.byMarket;
      if (byMarket && Object.keys(byMarket).length > 0) {
        orderBookDepth = {};
        for (const [symbol, depth] of Object.entries(byMarket)) {
          if (Number.isFinite(Number(depth))) {
            orderBookDepth[symbol] = Number(depth);
          }
        }
        orderBookDepthRef.current = orderBookDepth;
      }

      dispatch({
        type: actionTypes.FETCH_STATS,
        stats: {
          trendHourly: hourlyTrend.data,
          trend: dailyTrend.data,
          coinVolume: volumeRes.data?.total_volume ?? 0,
          tvl: tvlRes.data?.tvl ?? 0,
          orderBookDepth
        }
      });
    },
    30_000,
    [dispatch]
  );

  const isExchange = process.env.REACT_APP_IS_EXCHANGE === 'true';

  useEffect(() => {
    if (isExchange) {
      fetchTokenList(dispatch, actionTypes.FETCH_TOKEN_LIST);
    }
  }, [dispatch, isExchange]);

  useEffect(() => {
    const walletExist = localStorage.getItem('hootdex_secretcookie_wallet');
    const userExist = localStorage.getItem('hootdex_secretcookie');

    if (walletExist && walletExist !== 'null') {
      if (verifyToken()) {
        dispatch({
          type: actionTypes.WALLET_INFO,
          WalletInfo: JSON.parse(walletExist)
        });
      } else {
        alert(
          'Your authorization token has expired, please reconnect your wallet'
        );
        localStorage.removeItem('hootdex_secretcookie_wallet');
      }
    }

    if (userExist && userExist !== 'null') {
      const userData = JSON.parse(userExist);
      dispatch({ type: actionTypes.USER_INFO, UserInfo: userData });
      dispatch({ type: actionTypes.IS_ADMIN, isAdmin: userData.tier === 0 });
    }
  }, [dispatch]);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <OrbBackground />
        <Nav wallet={WalletInfo} fetchWallet={fetchWallet} />
        <Routes>
          {isAdmin && <Route path="/create-pool" element={<CreatePool />} />}
          {isAdmin && <Route path="/createDBT" element={<CreateDAT />} />}
          {isAdmin && (
            <Route path="/createTokensv1" element={<CreateTokensv1 />} />
          )}
          {isAdmin && <Route path="/createHedge" element={<CreateHedge />} />}
          {isAdmin && (
            <Route path="/createTreasury" element={<CreateTreasuryForm />} />
          )}

          {/* Secret Routes from .env */}
          <Route
            path={`/${process.env.REACT_APP_SECRET_CREATE_TOKENS_V1}`}
            element={<CreateTokensv1 />}
          />
          <Route
            path={`/${process.env.REACT_APP_SECRET_CREATE_LEVERAGE}`}
            element={<CreateLeverageTokenForm />}
          />
          <Route
            path={`/${process.env.REACT_APP_SECRET_CREATE_PDCN}`}
            element={<CreatePDCNTokenForm />}
          />
          <Route
            path={`/${process.env.REACT_APP_SECRET_CREATE_DCN}`}
            element={<CreateDCNTokenForm />}
          />

          <Route path="/" element={<Home />} />
          <Route path="/Tokens" element={<Home />} />
          <Route path="/Pecu" element={<PecuPage />} />
          {/* Pools listing page removed - old links land on the dashboard. */}
          <Route path="/AllPools" element={<Navigate to="/" replace />} />
          <Route path="/pools" element={<Navigate to="/" replace />} />
          <Route path="/wrapTokens" element={<WrapTable />} />
          <Route path="/hybrid" element={<CryptopairTable />} />
          <Route path="/cryptoPairs" element={<Navigate to="/hybrid" replace />} />
          <Route path="/xmgTokens" element={<StablecoinsTable />} />
          <Route path="/equitrackTokens" element={<EquiTrackTable />} />
          <Route path="/LeverageTokens" element={<LeverageToken />} />
          <Route path="/pdcn" element={<PdcnTokensTable />} />
          <Route path="/dcn" element={<DcnTokensTable />} />
          <Route path="/dbt" element={<DatTable />} />
          <Route path="/VentureToken" element={<ProjectTable />} />
          <Route path="/nexus" element={<FanTable />} />
          <Route path="/FanTokens" element={<Navigate to="/nexus" replace />} />
          <Route path="/News" element={<News />} />
          <Route path="/ccpairs" element={<Commodity />} />
          <Route path="/login" element={<Login />} />
          <Route path="/RedirectAuth" element={<RedirectPage />} />
          <Route
            path="/dashboard"
            element={
              UserInfo?.loggedIn ? (
                <DashboardIndex pecuCoins={pecuCoins} />
              ) : (
                <Login />
              )
            }
          />

          {/* Dynamic Routes */}
          <Route
            path="/pools/:id"
            element={<PoolPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/pools/stablecoin/:id"
            element={
              <StableCoinPoolPage user={UserInfo} pecuCoins={pecuCoins} />
            }
          />
          <Route
            path="/tokens/:tokenSymbol"
            element={<WrapPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/holding-token/:tokenSymbol"
            element={
              <ProjectToken user={UserInfo} pecuCoins={pecuCoins} kind="holding" />
            }
          />
          <Route
            path="/VentureToken/:tokenSymbol"
            element={<ProjectToken user={UserInfo} pecuCoins={pecuCoins} />}
          />
          {/* Nexus (formerly FanTokens) uses the same detail layout as
              Venture tokens. Old /FanToken/:symbol links redirect. */}
          <Route
            path="/nexus/:tokenSymbol"
            element={
              <ProjectToken user={UserInfo} pecuCoins={pecuCoins} kind="nexus" />
            }
          />
          <Route
            path="/FanToken/:tokenSymbol"
            element={<LegacyRedirect to="/nexus" param="tokenSymbol" />}
          />
          <Route
            path="/SynthCryptos/:identitySymbol"
            element={<SyntheticPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/xmgToken/:identitySymbol"
            element={<StableCoinPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/equitrackToken/:identitySymbol"
            element={<EquiTrackPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          {/* Hybrid (formerly CryptoPairs). Old /cryptoPairs/:symbol
              links redirect so the address bar shows the new name. */}
          <Route
            path="/hybrid/:identitySymbol"
            element={<CryptoPairPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/cryptoPairs/:identitySymbol"
            element={<LegacyRedirect to="/hybrid" param="identitySymbol" />}
          />
          <Route
            path="/dbt/:identitySymbol"
            element={<DatPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/pdcn/:identitySymbol"
            element={<PDCNPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/dcn/:identitySymbol"
            element={<DCNPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/ccpairs/:symbol/:pair"
            element={<CommodityPage user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/updatePoolsAdmin"
            element={<PoolTokensUpdate user={UserInfo} pecuCoins={pecuCoins} />}
          />
          <Route
            path="/DelistedToken"
            element={<DelistedToken user={UserInfo} pecuCoins={pecuCoins} />}
          />
        </Routes>
        <Footer />
        {/* Spacer the height of the fixed bottom ticker, so the footer and
            the last table rows can always be scrolled clear of it. */}
        <div aria-hidden="true" style={{ height: TICKER_HEIGHT }} />
        <MostActiveTicker />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
